import { useEffect, useState } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Box, Dialog, DialogContent, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateRangePicker } from "@mui/x-date-pickers-pro";

import {
  apiOutletClient,
  formatMoney,
  formatNumber,
  getTransactionName,
  getTransactionPath,
} from "../../shared";
import { mapTransactionsWithBalance } from "../utils/transactions-mapper";

export const WarehouseStockTransactionsDialog = ({
  warehouse,
  productVariant,
  onClose,
}) => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, "day"),
    dayjs(),
  ]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [warehouseStockTransactions, setWarehouseStockTransactions] = useState(
    []
  );

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const [startDate, endDate] = dateRange;
        if (!startDate || !endDate) return;

        const {
          data: { data, pageInfo },
        } = await apiOutletClient.get("warehouse-stock-transactions", {
          params: {
            sku: productVariant.sku,
            warehouseId: warehouse.id,
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
            page: paginationModel.page,
            size: paginationModel.pageSize,
            order: "ASC",
          },
        });

        setWarehouseStockTransactions(mapTransactionsWithBalance(data));
        setRowCount(pageInfo?.totalItems ?? 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [
    dateRange,
    productVariant.sku,
    warehouse,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogContent>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2
          }}>
          <Box>
            <Typography variant="body1">{productVariant.name}</Typography>
            <Typography variant="body2">Di Gudang {warehouse.name}</Typography>
          </Box>
          <DateRangePicker
            disableFuture
            format="DD MMM YYYY"
            localeText={{ start: "Dari", end: "Sampai" }}
            defaultValue={[dayjs().subtract(6, "day"), dayjs()]}
            onChange={(newRange) => {
              setDateRange(newRange);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            slotProps={{
              // The single-input field defaults too narrow and clips the end
              // date; give it room and match the app's small control size.
              textField: {
                size: "small",
                sx: { minWidth: 260 },
              },
            }}
          />
        </Stack>
        <DataGrid
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (value) =>
                value ? dayjs(value).format("DD MMM YYYY") : "-",
            },
            {
              field: "referenceNumber",
              headerName: "No. Sumber #",
              flex: 2,
              sortable: false,
              renderCell: ({ value }) => {
                if (!value) return "-";

                const path = getTransactionPath(value);
                if (!path) return value;

                return (
                  <a href={path} target="_blank" rel="noopener noreferrer">
                    {value}
                  </a>
                );
              },
            },
            {
              field: "transactionableType",
              headerName: "Tipe Transaksi",
              flex: 2,
              sortable: false,
              valueGetter: (value) => getTransactionName(value),
            },
            {
              field: "unitValue",
              headerName: "Nilai Satuan",
              type: "number",
              flex: 1.2,
              sortable: false,
              valueFormatter: (value) => (value == null ? "-" : formatMoney(value)),
            },
            {
              field: "quantityIn",
              headerName: "Masuk",
              type: "number",
              valueFormatter: (value) => formatNumber(value),
              flex: 1,
              sortable: false,
            },
            {
              field: "quantityOut",
              headerName: "Keluar",
              type: "number",
              valueFormatter: (value) => formatNumber(value),
              flex: 1,
              sortable: false,
            },
            {
              field: "balance",
              headerName: "Saldo",
              type: "number",
              flex: 1,
              sortable: false,
              valueFormatter: (value) => (value == null ? "-" : value),
            },
          ]}
          rows={warehouseStockTransactions}
          loading={loading}
          rowSelection={false}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      </DialogContent>
    </Dialog>
  );
};

WarehouseStockTransactionsDialog.propTypes = {
  warehouse: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  productVariant: PropTypes.shape({
    name: PropTypes.string,
    sku: PropTypes.string,
  }),
  onClose: PropTypes.func,
};
